import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Building2, Mail, Lock } from 'lucide-react';
import { authAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';

const Login = ({ setUser }) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);

  const handleDemoLogin = async (userType) => {
    setLoading(true);
    try {
      const response = await authAPI.demoLogin(userType);
      setUser(response.user);
      toast({
        title: "Login Successful",
        description: response.message,
        variant: "default"
      });
      navigate('/dashboard');
    } catch (error) {
      toast({
        title: "Login Failed",
        description: error.response?.data?.detail || "An error occurred during login",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-8">
        {/* Logo and Header */}
        <div className="text-center space-y-4">
          <div className="flex items-center justify-center space-x-2">
            {/* Menarini Logo */}
            <div className="bg-red-600 text-white px-3 py-2 font-bold text-xl">
              MENARINI
            </div>
            <div className="text-sm text-gray-500">group</div>
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Menarini Flow</h1>
            <p className="text-sm text-gray-600">Approval Management System</p>
          </div>
        </div>

        {/* Login Card */}
        <Card className="shadow-lg border-0 bg-white/80 backdrop-blur-sm">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-semibold">Sign in</CardTitle>
            <CardDescription>
              Enter your credentials to access the system
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Login Form */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="Enter your email"
                    className="pl-10"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="Enter your password"
                    className="pl-10"
                  />
                </div>
              </div>
              <Button className="w-full bg-red-600 hover:bg-red-700 text-white">
                Sign In
              </Button>
            </div>

            {/* Demo Buttons */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-2 text-gray-500">Demo Access</span>
              </div>
            </div>

            <div className="space-y-3">
              <Button
                variant="outline"
                className="w-full border-green-200 text-green-700 hover:bg-green-50 hover:border-green-300"
                onClick={() => handleDemoLogin('project-owner')}
                disabled={loading}
              >
                {loading ? 'Logging in...' : 'Log in as Project Owner'}
              </Button>
              <Button
                variant="outline"
                className="w-full border-purple-200 text-purple-700 hover:bg-purple-50 hover:border-purple-300"
                onClick={() => handleDemoLogin('approver')}
                disabled={loading}
              >
                {loading ? 'Logging in...' : 'Log in as Approver'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center text-sm text-gray-500 space-y-1">
          <p>© 2025 Menarini Group. All rights reserved.</p>
          <p className="text-xs">Powered by <span className="font-semibold text-blue-600">Voktech</span></p>
        </div>
      </div>
    </div>
  );
};

export default Login;